// Visual ground for actors (integration): the sim is planar, but some map views move their floor —
// the Tipsy Teacup saucer tilts up to 15° (≈ 2 m at the rim) and its cup holds SPUN beans. The
// Renderer passes every presented frame through `ActorLift`, which returns a copy whose beans and
// entities sit on the floor the map view drew: z + the surface offset at (x, y), fading out as a body
// drops toward the void plane (so falls still land on the flat void surface and KO splashes line
// up), and SPUN beans sunk into the cup instead of floating at the rim height they ride at in the sim.
// Untilted maps pass the frame through untouched (no copy, no cost).
import { MAX_BEANS, TUNE } from '@shared/constants';
import { BS } from '@shared/ids';
import { createEntityView, createFrameView, type EntityView, type FrameView } from '../game/FrameView';
import { copyBean } from './vfx/Hitstop';

/** How deep a SPUN bean sits in the cup (its ride height is the cup top, 0.8 m). */
const CUP_SINK = 0.48;
/** Rate (1/s) at which the sink eases in and out (a fling pops out over ~0.1 s). */
const SINK_RATE = 22;

/** Surface height offset at sim (x, y), e.g. `MapView.surfaceOffset`. */
export type SurfaceOffset = (x: number, y: number) => number;

/** Weight of the surface offset at height z: 1 on or above the floor, 0 at the void plane. */
export function liftWeight(z: number): number {
  const kill = TUNE.edge.killZ;
  return z >= 0 ? 1 : z <= kill ? 0 : 1 - z / kill;
}

export function copyEntity(dst: EntityView, src: EntityView): void {
  dst.id = src.id; dst.kind = src.kind; dst.owner = src.owner; dst.x = src.x; dst.y = src.y; dst.z = src.z;
  dst.vx = src.vx; dst.vy = src.vy; dst.vz = src.vz; dst.t = src.t; dst.timer = src.timer; dst.data = src.data;
  dst.data2 = src.data2; dst.pending = src.pending;
}

export class ActorLift {
  private readonly out = createFrameView();
  private readonly pool: EntityView[] = [];
  private readonly ents: EntityView[] = [];
  private readonly sink = new Float64Array(MAX_BEANS);
  /** Surface gradient (height per metre along sim x and y) of the last lifted frame; 0 when flat. */
  gx = 0;
  gy = 0;

  /** Clears the cup state (new round or map). */
  reset(): void {
    this.sink.fill(0);
    this.gx = 0;
    this.gy = 0;
  }

  /**
   * The frame to present: `pv` itself when nothing moves the floor, else a copy with lifted actors.
   * `offset` is linear (a tilted plane through the origin), `dtSec` real seconds since the last frame.
   */
  apply(pv: FrameView, offset: SurfaceOffset | undefined, dtSec: number): FrameView {
    let gx = 0;
    let gy = 0;
    if (offset) {
      const o0 = offset(0, 0);
      gx = offset(1, 0) - o0;
      gy = offset(0, 1) - o0;
    }
    this.gx = gx;
    this.gy = gy;
    const k = 1 - Math.exp(-SINK_RATE * Math.max(0, dtSec));
    let sinking = false;
    for (let s = 0; s < MAX_BEANS; s++) {
      const b = pv.beans[s];
      const want = b && b.active && b.state === BS.SPUN ? CUP_SINK : 0;
      this.sink[s] = pv.source === 'replay' || dtSec <= 0 ? want : this.sink[s] + (want - this.sink[s]) * k;
      if (this.sink[s] < 1e-4) this.sink[s] = 0;
      if (this.sink[s] > 0) sinking = true;
    }
    const tilted = Math.abs(gx) + Math.abs(gy) > 1e-6;
    if (!tilted && !sinking) return pv;

    const v = this.out;
    v.now = pv.now; v.dt = pv.dt; v.timeScale = pv.timeScale; v.source = pv.source;
    v.roomPhase = pv.roomPhase; v.mapId = pv.mapId; v.modeId = pv.modeId; v.golden = pv.golden; v.mutator = pv.mutator;
    v.roundIndex = pv.roundIndex; v.totalRounds = pv.totalRounds; v.roundId = pv.roundId;
    v.renderTick = pv.renderTick; v.localTick = pv.localTick; v.roundTick = pv.roundTick;
    v.clock = pv.clock; v.localSlot = pv.localSlot; v.focusSlot = pv.focusSlot;
    v.map = pv.map; v.hazard = pv.hazard; v.mode = pv.mode; v.net = pv.net;
    v.replay = pv.replay; v.excitement = pv.excitement; v.grade = pv.grade;
    for (let s = 0; s < MAX_BEANS; s++) {
      const src = pv.beans[s];
      const dst = v.beans[s];
      copyBean(dst, src);
      if (!src.active) continue;
      if (tilted) dst.z += liftWeight(src.z) * (gx * src.x + gy * src.y);
      dst.z -= this.sink[s];
    }
    const n = pv.ents.length;
    while (this.pool.length < n) this.pool.push(createEntityView());
    this.ents.length = n;
    for (let i = 0; i < n; i++) {
      const src = pv.ents[i];
      const dst = this.pool[i];
      this.ents[i] = dst;
      copyEntity(dst, src);
      if (tilted) dst.z += liftWeight(src.z) * (gx * src.x + gy * src.y);
    }
    v.ents = this.ents;
    return v;
  }
}
